import { createElement } from "react";

import DescriptionIcon from "@mui/icons-material/Description";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Collapse,
  TextField,
  Typography,
} from "@mui/material";
import {
  postGenerateNarrativeFromSurveyJson,
  postSubmissionBreachStatementPublic,
} from "@rubi/api-generated";
import { colors } from "@rubi/ui";
import { toast } from "@rubi/ui";
import ReactDOMServer from "react-dom/server";
import {
  ElementFactory,
  Question,
  Serializer,
  SurveyError,
  type SurveyModel,
  SvgRegistry,
} from "survey-core";
import { localization } from "survey-creator-core";
import {
  ReactQuestionFactory,
  SurveyQuestionElementBase,
} from "survey-react-ui";

import { tQuestion } from "@/components/online-reporting/custom-questions/i18nQuestion";

const NARRATIVE_CANVAS_TYPE = "narrative-canvas";

// Regex pattern to detect placeholders in the format [* ... *]
const PLACEHOLDER_PATTERN = /\[\*[^\]]*\*\]/g;

export class NarrativeCanvasModel extends Question {
  getType() {
    return NARRATIVE_CANVAS_TYPE;
  }

  get instructions() {
    return this.getPropertyValue("instructions") as string;
  }
  set instructions(val: string) {
    this.setPropertyValue("instructions", val);
  }

  get placeholder() {
    return this.getPropertyValue("placeholder") as string;
  }
  set placeholder(val: string) {
    this.setPropertyValue("placeholder", val);
  }

  get exampleNarrative() {
    return this.getPropertyValue("exampleNarrative") as string;
  }
  set exampleNarrative(val: string) {
    this.setPropertyValue("exampleNarrative", val);
  }

  get instructionItems() {
    return this.getPropertyValue("instructionItems") as string;
  }
  set instructionItems(val: string) {
    this.setPropertyValue("instructionItems", val);
  }

  get generator() {
    return this.getPropertyValue("generator") as string;
  }
  set generator(val: string) {
    this.setPropertyValue("generator", val);
  }

  get submissionId() {
    return this.getPropertyValue("submissionId") as string;
  }
  set submissionId(val: string) {
    this.setPropertyValue("submissionId", val);
  }

  /**
   * Data key holding this narrative's acknowledgment, e.g. `incident_narrativeAcknowledged`.
   * The condensed-report transformer derives the same key from the question name so the
   * portal can show the acknowledgment as its own field — keep the two in step.
   */
  get acknowledgmentName(): string {
    return `${this.name}Acknowledged`;
  }

  /**
   * Whether the reporting party has signed off on the narrative.
   *
   * Held in the survey's data rather than as a question property so it is actually saved
   * with the submission. As a property it lived only in the browser: it never reached the
   * draft, so it was lost on resume and the portal had no way to show whether the reporting
   * party had accepted the words as their own — which is the point of asking.
   */
  get narrativeAcknowledged(): boolean {
    return (
      (this.survey as SurveyModel | null)?.getValue(this.acknowledgmentName) ===
      true
    );
  }
  set narrativeAcknowledged(val: boolean) {
    (this.survey as SurveyModel | null)?.setValue(this.acknowledgmentName, val);
  }

  protected onValueChanged() {
    super.onValueChanged();
    this.validate();
  }

  // SurveyJS Next/Preview call hasErrors → onCheckForErrors, not a Question.validate()
  // override. Errors added only in validate() were wiped on navigation, so the
  // acknowledgment checkbox never blocked the page.
  protected onCheckForErrors(
    errors: Array<SurveyError>,
    isOnValueChanged: boolean,
    fireCallback: boolean,
  ): void {
    super.onCheckForErrors(errors, isOnValueChanged, fireCallback);

    const value = (this.value as string) || "";
    PLACEHOLDER_PATTERN.lastIndex = 0;
    if (PLACEHOLDER_PATTERN.test(value)) {
      errors.push(
        new SurveyError(
          tQuestion("narrativeQuestion.removePlaceholdersError"),
          this,
        ),
      );
    }
    if (!this.narrativeAcknowledged) {
      errors.push(
        new SurveyError(
          tQuestion("narrativeQuestion.confirmReviewedError"),
          this,
        ),
      );
    }
  }
}

// Guard against duplicate registration (can happen with HMR or multiple imports)
export function registerNarrativeCanvas() {
  if (!ElementFactory.Instance.getAllTypes().includes(NARRATIVE_CANVAS_TYPE)) {
    ElementFactory.Instance.registerElement(NARRATIVE_CANVAS_TYPE, (name) => {
      return new NarrativeCanvasModel(name);
    });
  }
}

// Specify display names for the question type
const locale = localization.getLocale("");
locale.qt[NARRATIVE_CANVAS_TYPE] = "Narrative Canvas"; // i18n: builder label deferred

// Register an SVG icon for the question type
const SURVEY_ICON_COLOR = colors.text.disabled;
const svg = ReactDOMServer.renderToString(
  <DescriptionIcon style={{ fill: SURVEY_ICON_COLOR }} />,
);
SvgRegistry.registerIconFromSvg(NARRATIVE_CANVAS_TYPE, svg);

// Guard against duplicate registration (can happen with HMR or multiple imports)
if (!Serializer.findClass(NARRATIVE_CANVAS_TYPE)) {
  Serializer.addClass(
    NARRATIVE_CANVAS_TYPE,
    [
      { name: "instructions", category: "general", default: "" },
      { name: "instructionItems", category: "general", default: "" },
      {
        name: "placeholder",
        category: "general",
        default: "",
      },
      { name: "exampleNarrative", category: "general", default: "" },
      { name: "generator", category: "general", default: "" },
      { name: "submissionId", category: "general", default: "" },
    ],
    function () {
      return new NarrativeCanvasModel("");
    },
    "question",
  );
}

class SurveyQuestionNarrativeCanvas extends SurveyQuestionElementBase {
  private isLoading: boolean = false;
  private tipsOpen: boolean = false;

  protected get question() {
    return this.props.question as NarrativeCanvasModel;
  }

  componentDidMount() {
    // A report opened for viewing is in display mode. Generating there would rewrite a
    // submitted statement and call the reporting-side endpoint with no modification token.
    if (this.question.isReadOnly) {
      this.updateValidation();
      return;
    }
    if (!this.question.value || (this.question.value as string).trim() === "") {
      void this.fillNarrative();
    } else {
      this.updateValidation();
    }
  }

  private fillNarrative = async () => {
    const submissionId = this.question.submissionId;
    if (!submissionId) {
      toast.error(tQuestion("narrativeQuestion.generateFailed"));
      return;
    }

    this.isLoading = true;
    this.forceUpdate();

    try {
      const response =
        this.question.generator === "breach-statement"
          ? await postSubmissionBreachStatementPublic({
              path: { submission_id: submissionId },
              // The answers as they stand on screen. The draft save is debounced and fire-
              // and-forget, and the supervisor's notes are entered on the page immediately
              // before this one, so reading the stored draft would race the save.
              body: {
                data: (this.question.survey as SurveyModel | null)?.data ?? {},
              },
              throwOnError: false,
            })
          : await postGenerateNarrativeFromSurveyJson({
              path: { submission_id: submissionId },
              throwOnError: false,
            });
      const narrative =
        response.data && "statement" in response.data
          ? response.data.statement
          : response.data?.narrative;

      if (narrative) {
        this.question.value = narrative;
        this.question.narrativeAcknowledged = false;
        this.updateValidation();
      } else {
        toast.error(tQuestion("narrativeQuestion.generateFailed"));
      }
    } catch (error: unknown) {
      console.error("Failed to generate narrative:", error);
      toast.error(tQuestion("narrativeQuestion.generateFailed"));
    } finally {
      this.isLoading = false;
      this.forceUpdate();
    }
  };

  private hasPlaceholderText = (text: string): boolean => {
    // Reset regex lastIndex to ensure accurate testing
    PLACEHOLDER_PATTERN.lastIndex = 0;
    return PLACEHOLDER_PATTERN.test(text);
  };

  private updateValidation = () => {
    // Trigger SurveyJS validation which will use the validate() method in the Question class
    this.question.validate();
    this.forceUpdate();
  };

  renderElement() {
    const value = (this.question.value as string) || "";
    const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      this.question.value = e.target.value;
      // Update validation when text changes
      this.updateValidation();
    };

    const handleAcknowledgmentChange = (
      e: React.ChangeEvent<HTMLInputElement>,
    ) => {
      this.question.narrativeAcknowledged = e.target.checked;
      this.updateValidation();
    };

    const isGenerating = this.isLoading;
    // SurveyJS display mode, an explicitly read-only question, or a read-only parent panel.
    const isReadOnly = this.question.isReadOnly;
    const hasPlaceholder = this.hasPlaceholderText(value);
    const isAcknowledged = this.question.narrativeAcknowledged;
    const showTips = this.question.generator !== "breach-statement";

    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 2,
          minHeight: 400,
          maxWidth: "100%",
          width: "100%",
        }}
      >
        <Typography
          variant="body2"
          sx={{
            color: "text.secondary",
            fontSize: { xs: 13, md: 14 },
            lineHeight: 1.6,
            mb: 1,
            wordBreak: "break-word",
            overflowWrap: "break-word",
            whiteSpace: "normal",
          }}
        >
          {tQuestion("narrativeQuestion.introHelper")}
        </Typography>
        {(isGenerating || showTips) && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 2,
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                flex: 1,
              }}
            >
              {isGenerating && (
                <>
                  <CircularProgress size={20} sx={{ color: "primary.main" }} />
                  <Typography
                    variant="body2"
                    sx={{
                      color: "text.secondary",
                      fontSize: { xs: 13, md: 14 },
                    }}
                  >
                    {tQuestion("narrativeQuestion.generating")}
                  </Typography>
                </>
              )}
            </Box>
            {showTips && (
              <Button
                variant="outlined"
                onClick={() => {
                  this.tipsOpen = !this.tipsOpen;
                  this.forceUpdate();
                }}
                sx={{
                  textTransform: "none",
                  fontWeight: 600,
                  fontSize: { xs: 13, md: 14 },
                  px: 2.5,
                  py: 1.25,
                  borderWidth: 1.5,
                  borderRadius: 2,
                  whiteSpace: "nowrap",
                  "&:hover": {
                    borderWidth: 1.5,
                    bgcolor: "primary.50",
                  },
                }}
              >
                {tQuestion("narrativeQuestion.tipsButton")}
                {this.tipsOpen ? (
                  <ExpandLessIcon sx={{ ml: 0.5, fontSize: 18 }} />
                ) : (
                  <ExpandMoreIcon sx={{ ml: 0.5, fontSize: 18 }} />
                )}
              </Button>
            )}
          </Box>
        )}
        <Collapse in={showTips && this.tipsOpen}>
          <Box
            sx={{
              p: 2.5,
              bgcolor: "info.50",
              borderRadius: 2,
              border: "1px solid",
              borderColor: "info.200",
              mb: 1,
              wordBreak: "break-word",
              overflowWrap: "break-word",
              whiteSpace: "normal",
            }}
          >
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: 600,
                fontSize: { xs: 14, md: 15 },
                mb: 1.5,
                color: "info.dark",
                wordBreak: "break-word",
                overflowWrap: "break-word",
                whiteSpace: "normal",
              }}
            >
              {tQuestion("narrativeQuestion.tipsHeading")}
            </Typography>
            <Box
              component="ul"
              sx={{
                margin: 0,
                paddingLeft: 2.5,
                wordBreak: "break-word",
                overflowWrap: "break-word",
                whiteSpace: "normal",
                "& li": {
                  marginBottom: 1,
                  fontSize: { xs: 13, md: 14 },
                  lineHeight: 1.7,
                  color: "text.primary",
                  wordBreak: "break-word",
                  overflowWrap: "break-word",
                  whiteSpace: "normal",
                },
              }}
            >
              <li>
                <strong>
                  {tQuestion("narrativeQuestion.tipBeSpecificLabel")}
                </strong>{" "}
                {tQuestion("narrativeQuestion.tipBeSpecificBody")}
              </li>
              <li>
                <strong>
                  {tQuestion("narrativeQuestion.tipChronologicalLabel")}
                </strong>{" "}
                {tQuestion("narrativeQuestion.tipChronologicalBody")}
              </li>
              <li>
                <strong>
                  {tQuestion("narrativeQuestion.tipRelevantDetailsLabel")}
                </strong>{" "}
                {tQuestion("narrativeQuestion.tipRelevantDetailsBody")}
              </li>
              <li>
                <strong>
                  {tQuestion("narrativeQuestion.tipFactualLabel")}
                </strong>{" "}
                {tQuestion("narrativeQuestion.tipFactualBody")}
              </li>
              <li>
                <strong>
                  {tQuestion("narrativeQuestion.tipClearLanguageLabel")}
                </strong>{" "}
                {tQuestion("narrativeQuestion.tipClearLanguageBody")}
              </li>
              <li>
                <strong>
                  {tQuestion("narrativeQuestion.tipCompletenessLabel")}
                </strong>{" "}
                {tQuestion("narrativeQuestion.tipCompletenessBody")}
              </li>
            </Box>
          </Box>
        </Collapse>
        <TextField
          multiline
          fullWidth
          value={value}
          onChange={onChange}
          placeholder={
            this.question.placeholder ||
            tQuestion("narrativeQuestion.placeholder")
          }
          rows={16}
          variant="outlined"
          disabled={isGenerating}
          // Read-only rather than disabled so an officer can still select and copy the
          // statement out of a submitted report.
          InputProps={{ readOnly: isReadOnly }}
          inputProps={{
            style: {
              whiteSpace: "pre-wrap",
              wordWrap: "break-word",
            },
          }}
          sx={{
            flex: 1,
            "& .MuiOutlinedInput-root": {
              minHeight: { xs: 260, md: 350 },
              fontSize: { xs: 14, md: 15 },
              "& fieldset": {
                borderWidth: 1.5,
                borderColor: hasPlaceholder ? "error.main" : "grey.300",
                borderRadius: 2,
              },
              "&:hover fieldset": {
                borderColor: hasPlaceholder ? "error.main" : "primary.main",
              },
              "&.Mui-focused fieldset": {
                borderColor: hasPlaceholder ? "error.main" : "primary.main",
                borderWidth: 2,
              },
            },
            "& .MuiInputBase-input": {
              padding: { xs: 2.5, md: 3 },
              lineHeight: 1.7,
              whiteSpace: "pre-wrap",
              wordWrap: "break-word",
            },
          }}
        />
        {hasPlaceholder && (
          <Box
            sx={{
              p: 2,
              bgcolor: "error.50",
              borderRadius: 2,
              border: "1px solid",
              borderColor: "error.200",
            }}
          >
            <Typography
              variant="body2"
              sx={{
                color: "error.main",
                fontSize: { xs: 13, md: 14 },
                fontWeight: 500,
                wordBreak: "break-word",
                overflowWrap: "break-word",
                whiteSpace: "normal",
              }}
            >
              <strong>
                {tQuestion("narrativeQuestion.removePlaceholdersHelper")}
              </strong>
            </Typography>
          </Box>
        )}
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            mt: 1,
            gap: 1.5,
            width: "100%",
          }}
        >
          <Checkbox
            checked={isAcknowledged}
            onChange={handleAcknowledgmentChange}
            disabled={isGenerating || hasPlaceholder || isReadOnly}
            sx={{
              color: "primary.main",
              padding: "9px",
              marginTop: "2px",
              flexShrink: 0,
              "&.Mui-checked": {
                color: "primary.main",
              },
            }}
          />
          <Typography
            variant="body2"
            component="div"
            sx={{
              fontSize: { xs: 14, md: 15 },
              color: "text.primary",
              userSelect: "none",
              lineHeight: 1.6,
              flex: 1,
              minWidth: 0,
              wordBreak: "break-word",
              overflowWrap: "break-word",
              whiteSpace: "normal",
            }}
          >
            {tQuestion("narrativeQuestion.acknowledgment")}
          </Typography>
        </Box>
      </Box>
    );
  }
}

ReactQuestionFactory.Instance.registerQuestion(
  NARRATIVE_CANVAS_TYPE,
  (props) => {
    return createElement(SurveyQuestionNarrativeCanvas, props);
  },
);

export const NARRATIVE_CANVAS = NARRATIVE_CANVAS_TYPE;
